// Homestead rendering, both sides of the door.
//
// HomesteadLayer (surface): claim stakes — a brass post with the name on a
// plaque — and structure shells: timber walls on a stone plinth that runs
// from the highest corner's floor plate down past the lowest ground
// sample, so a cabin on a ridgeland slope stands on masonry instead of
// floating on air. The plinth IS the terrain-fit story: the shared
// foundationFit that placed the floor is the same arithmetic that sizes
// the skirt.
//
// HomeInteriorLayer (home rooms): the furniture, from the replicated
// rows, plus the set-down ghost — a floor patch that warms where the
// carried piece would land and sours where it would be refused, computed
// with the exact shared arithmetic the server will rule with. A picture,
// not a number.

import {
  anchorForPlacement,
  foundationFit,
  type HomeGrid,
  heightAt,
  homeGrid,
  isHomeRoomId,
  isPlaceableKind,
  PLACEABLE_SPECS,
  type PlaceableKind,
  type PlayerPose,
  parseHomeRoomId,
  placeableAabb,
  placeableCells,
  placementRefusal,
  quarterFacing,
  quarterFromYaw,
  type StructureKind,
  structureDoorPos,
  structureFootprint,
} from "@bebop/shared";
import * as THREE from "three";
import type { RemotePlaceable, RemoteStructure, World } from "../core/world";
import { disposeAuthoredObject, instanceAuthoredAsset } from "./authored-assets";
import { castsShadows, signTexture, Tracker } from "./common";

const PLINTH = 0x3a3a40;
const WINDOW_GLOW = 0xffc46b;

interface StakeEntry {
  root: THREE.Group;
  tracker: Tracker;
  name: string;
}

interface ShellEntry {
  root: THREE.Group;
  tracker: Tracker;
  claimName: string;
  door: string;
}

export class HomesteadLayer {
  readonly group = new THREE.Group();
  private stakes = new Map<string, StakeEntry>();
  private shells = new Map<string, ShellEntry>();

  sync(world: World): void {
    for (const [id, entry] of this.stakes) {
      const claim = world.claims.get(id);
      if (!claim || claim.name !== entry.name) {
        this.group.remove(entry.root);
        entry.tracker.dispose();
        this.stakes.delete(id);
      }
    }
    for (const [id, claim] of world.claims) {
      if (this.stakes.has(id)) continue;
      const entry = buildStake(claim.name, claim.x, claim.z);
      this.stakes.set(id, entry);
      castsShadows(entry.root);
      this.group.add(entry.root);
    }

    for (const [id, entry] of this.shells) {
      const structure = world.structures.get(id);
      if (!structure || structure.claimName !== entry.claimName || structure.door !== entry.door) {
        this.group.remove(entry.root);
        entry.tracker.dispose();
        this.shells.delete(id);
      }
    }
    for (const [id, structure] of world.structures) {
      if (this.shells.has(id)) continue;
      const entry = buildShell(structure);
      this.shells.set(id, entry);
      castsShadows(entry.root);
      this.group.add(entry.root);
    }
  }

  dispose(): void {
    for (const entry of this.stakes.values()) {
      this.group.remove(entry.root);
      entry.tracker.dispose();
    }
    for (const entry of this.shells.values()) {
      this.group.remove(entry.root);
      entry.tracker.dispose();
    }
    this.stakes.clear();
    this.shells.clear();
  }
}

/** A claim stake: brass post, small cap, and the name as a plaque. */
function buildStake(name: string, x: number, z: number): StakeEntry {
  const t = new Tracker();
  const root = new THREE.Group();
  const ground = heightAt(x, z);

  const stake = instanceAuthoredAsset("claim-stake", [{ x, y: ground, z }]);
  root.add(stake);
  t.cleanup(() => disposeAuthoredObject(stake));

  const plaqueTex = signTexture(t, name, "#d8b04a");
  const plaque = new THREE.Sprite(
    t.mat(new THREE.SpriteMaterial({ map: plaqueTex, transparent: true, depthWrite: false })),
  );
  plaque.scale.set(2.6, 0.49, 1);
  plaque.position.set(x, ground + 2.0, z);
  root.add(plaque);

  return { root, tracker: t, name };
}

/**
 * A structure shell. Terrain fit is the shared foundationFit: floor plate
 * at fit.floorY, plinth from below the lowest ground sample up to the
 * plate — no corner floats, at any legal grade, by construction.
 */
function buildShell(structure: RemoteStructure): ShellEntry {
  const t = new Tracker();
  const root = new THREE.Group();
  const { kind, x, z, quarter } = structure;
  const box = structureFootprint(kind, x, z, quarter);
  const fit = foundationFit(kind, x, z, quarter);
  const w = box.maxX - box.minX;
  const d = box.maxZ - box.minZ;

  const plinthMat = t.mat(new THREE.MeshStandardMaterial({ color: PLINTH, roughness: 0.95 }));
  // Plinth: from below the lowest ground to the floor plate.
  const plinthBase = fit.bottom - 0.4;
  const plinth = new THREE.Mesh(
    t.geo(new THREE.BoxGeometry(w, fit.floorY - plinthBase, d)),
    plinthMat,
  );
  plinth.position.set(x, (fit.floorY + plinthBase) / 2, z);
  root.add(plinth);

  // The terrain-fitted masonry remains parametric. The shell above it is
  // the editable Blender source, turned as one object around the shared door.
  const shell = instanceAuthoredAsset(kind === "cabin" ? "cabin-shell" : "shed-shell", [
    { x, y: fit.floorY, z, yaw: (quarter * Math.PI) / 2 },
  ]);
  root.add(shell);
  t.cleanup(() => disposeAuthoredObject(shell));
  const door = structureDoorPos(kind, x, z, quarter);
  const facing = quarterFacing(quarter);

  // The name over the door, small — read up close, not across a valley.
  const sign = signTexture(t, structure.claimName, "#d8b04a");
  const board = new THREE.Sprite(
    t.mat(new THREE.SpriteMaterial({ map: sign, transparent: true, depthWrite: false })),
  );
  board.scale.set(3.4, 0.64, 1);
  board.position.set(door.x + facing.x * 0.6, fit.floorY + 2.9, door.z + facing.z * 0.6);
  root.add(board);

  return { root, tracker: t, claimName: structure.claimName, door: structure.door };
}

// ---------------------------------------------------------------------------
// Interior furniture.

interface PlaceableEntry {
  root: THREE.Group;
  tracker: Tracker;
  key: string;
}

/** Everything that identifies a piece's rendered form. */
function placeableKey(p: RemotePlaceable): string {
  return `${p.kind}:${p.cellX}:${p.cellZ}:${p.rot}:${p.carried}`;
}

export class HomeInteriorLayer {
  readonly group = new THREE.Group();
  private entries = new Map<string, PlaceableEntry>();
  private ghost: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> | undefined;
  private ghostTracker: Tracker | undefined;

  sync(world: World, selfPose: PlayerPose | undefined): void {
    const parsed = isHomeRoomId(world.roomId) ? parseHomeRoomId(world.roomId) : undefined;
    if (!parsed) {
      this.clear();
      return;
    }
    const grid = homeGrid(parsed.kind);

    for (const [id, entry] of this.entries) {
      const placeable = world.placeables.get(id);
      if (!placeable || placeableKey(placeable) !== entry.key) {
        this.group.remove(entry.root);
        entry.tracker.dispose();
        this.entries.delete(id);
      }
    }
    for (const [id, placeable] of world.placeables) {
      if (this.entries.has(id) || placeable.carried) continue;
      if (!isPlaceableKind(placeable.kind)) continue;
      const entry = buildPlaceable(grid, placeable, placeable.kind);
      this.entries.set(id, entry);
      castsShadows(entry.root);
      this.group.add(entry.root);
    }

    this.syncGhost(world, parsed.kind, grid, selfPose);
  }

  /** The set-down ghost: where the carried piece would land, and how. */
  private syncGhost(
    world: World,
    structureKind: StructureKind,
    grid: HomeGrid,
    selfPose: PlayerPose | undefined,
  ): void {
    const carried = world.carriedPlaceable();
    if (!carried || !selfPose || !isPlaceableKind(carried.kind)) {
      if (this.ghost) this.ghost.visible = false;
      return;
    }
    const rot = quarterFromYaw(selfPose.yaw);
    const anchor = anchorForPlacement(grid, carried.kind, rot, selfPose.x, selfPose.z);
    const others = [...world.placeables.entries()]
      .filter(([id]) => id !== carried.id)
      .map(([, p]) => p);
    const refusal = placementRefusal(
      structureKind,
      carried.kind,
      anchor.cellX,
      anchor.cellZ,
      rot,
      others,
    );
    const cells = placeableCells(carried.kind, rot);

    if (!this.ghost) {
      this.ghostTracker = new Tracker();
      this.ghost = new THREE.Mesh(
        this.ghostTracker.geo(new THREE.PlaneGeometry(1, 1)),
        this.ghostTracker.mat(
          new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.4, depthWrite: false }),
        ),
      );
      this.ghost.rotation.x = -Math.PI / 2;
      this.group.add(this.ghost);
    }
    this.ghost.visible = true;
    this.ghost.scale.set(cells.w, cells.d, 1);
    const centerX = grid.minX + (anchor.cellX + cells.w / 2) * 1;
    const centerZ = grid.minZ + (anchor.cellZ + cells.d / 2) * 1;
    this.ghost.position.set(centerX, 0.03, centerZ);
    // Warm where it would land, sour where it would be refused. A colour,
    // not a number.
    this.ghost.material.color.setHex(refusal ? 0x8a3a30 : 0xd8b04a);
  }

  private clear(): void {
    for (const entry of this.entries.values()) {
      this.group.remove(entry.root);
      entry.tracker.dispose();
    }
    this.entries.clear();
    if (this.ghost) this.ghost.visible = false;
  }

  dispose(): void {
    this.clear();
    if (this.ghost) this.group.remove(this.ghost);
    this.ghostTracker?.dispose();
    this.ghost = undefined;
    this.ghostTracker = undefined;
  }
}

/** Source furniture is placed using the same grid, AABB and quarter turn. */
function buildPlaceable(
  grid: HomeGrid,
  placeable: RemotePlaceable,
  kind: PlaceableKind,
): PlaceableEntry {
  const t = new Tracker();
  const root = new THREE.Group();
  const box = placeableAabb(grid, kind, placeable.cellX, placeable.cellZ, placeable.rot);
  const cx = (box.minX + box.maxX) / 2;
  const cz = (box.minZ + box.maxZ) / 2;
  const spec = PLACEABLE_SPECS[kind];
  // The model is fitted in LOCAL coordinates, then rotated; rotated AABB
  // widths would swap the dimensions twice for long cots, rugs and benches.
  const furniture = instanceAuthoredAsset(kind, [
    {
      x: cx,
      y: 0,
      z: cz,
      width: spec.w - 0.12,
      depth: spec.d - 0.12,
      yaw: (placeable.rot * Math.PI) / 2,
    },
  ]);
  root.add(furniture);
  t.cleanup(() => disposeAuthoredObject(furniture));
  if (kind === "lamp") {
    const light = new THREE.PointLight(WINDOW_GLOW, 14, 9, 1.6);
    light.position.set(cx, 1.5, cz);
    root.add(light);
  }
  return { root, tracker: t, key: placeableKey(placeable) };
}
